import { css } from '@emotion/core';
import { Checkbox, Col, Collapse, Icon, Input, Row, Select, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import Button from '@/src/shared/components/Button';
import DateRangePicker from '@/src/shared/components/DateRangePicker';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import SearchTag from '@/src/shared/components/SearchTag';
import { bp, colors, form as formStyle, space } from '@/styles';
import { URL_DATE_FORMAT, validateCustomRange } from '@/utils/dateTimeHelpers';
import formatURL from '@/utils/formatURL';
import { escapeGTMParam, trackGTMEvent } from '@/utils/utils';

const { m } = space;

const filterColorOverride = css`
  background-color: #fff
`

const { Panel } = Collapse;

const style = {
  filterWrapper: css`
    @media ${bp.md} {
      width: 100%;
    }
  `,
  filterSearch: css`
    display: flex;
    border-bottom: 1px solid ${colors.accentGrayLight};
    padding-bottom: 20px;
  `,
  filters: css`
    padding: 4px 18px 4px;
    margin-bottom: 24px;
    margin-left: -12px;
    margin-right: -12px;
    background: #fff;
    box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);
    border-radius: 0px;
    @media ${bp.md} {
      padding: 8px 32px 4px;
      border-radius: 4px;
      margin-left: initial;
      margin-right: initial;
    }
    .ant-collapse {
      &-header {
        padding: 20px 0 !important;
      }
      &-content-box {
        padding: 0;
        .ant-collapse-content-box {
          padding: 0 0 20px !important;
        }
      }
      &-arrow {
        right: 0 !important;
        left: auto !important;
      }
    }
    & > div:first-of-type {
      margin-bottom: 20px;
      & > div {
        border-bottom: none !important;
        & > div:first-of-type {
          border-bottom: 1px solid ${colors.accentGrayLight};
          margin-bottom: 16px;
        }
      }
    }
  `,
  filterTags: css`
    visibility: visible;
    opacity: 1;
    transition: all 0.3s $ease-in-out;
    margin-bottom: 12px;
  `,
  filterTagsHidden: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    position: absolute;
  `,
  filterTagsVisible: css``,
  buttonContainer: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  `,
  myCommitteesLabel: css`
    font-size: 1.2rem;
    font-weight: 900;
  `,
  toolTipIcon: css`
    color: ${colors.secondaryBlue};
    font-size: 1.4rem;
    margin-left: 0.8rem;
  `,
};

const filterCollapseProps = {
  bordered: false,
  expandIconPosition: 'right',
  expandIcon: ({ isActive }) => <Icon type={isActive ? 'minus' : 'plus'} />,
};

const FIELD_NAMES = {
  SEARCHTEXT: 'searchText',
  DATERANGES: 'dateRanges',
  DATESTART: 'customRange',
  DATEEND: 'customRange',
  MEETINGTYPES: 'meetingTypes',
  STATES: 'states',
  COMMITTEES: 'committees',
};

const DESCRIPTION_NAME = {
  DATERANGES: 'DayRangeDescription',
  MEETINGTYPES: 'MeetingTypeDescription',
  STATES: 'StateDescription',
  COMMITTEES: 'CommitteeDescription',
};

const INITIAL_TAG_STATE = {
  [`tags_${FIELD_NAMES.SEARCHTEXT}`]: [],
  [`tags_${FIELD_NAMES.DATERANGES}`]: [],
  tags_customRange: [],
  [`tags_${FIELD_NAMES.MEETINGTYPES}`]: [],
  [`tags_${FIELD_NAMES.STATES}`]: [],
  [`tags_${FIELD_NAMES.COMMITTEES}`]: [],
};

const FilterSection = props => {
  const { label, children, defaultActiveKey, keyProp, ..._props } = props;
  const collapseProps = {
    ...filterCollapseProps,
    ..._props,
  };
  return (
    <Collapse {...collapseProps} defaultActiveKey={defaultActiveKey} css={filterColorOverride}>
      <Collapse.Panel
        key={keyProp}
        header={
          <Heading type={4} weight="bolder" theme="primaryGray">
            {label}
          </Heading>
        }
      >
        {children}
      </Collapse.Panel>
    </Collapse>
  );
};

const FILTER_KEY = '1';

const EventsSearchFilters = props => {
  const router = useRouter();
  const { query } = router;
  const {
    form,
    DateRanges,
    MeetingTypes,
    States,
    Committees,
    search,
    collapsed,
    setCollapsed,
    userCommittees,
  } = props;

  const [tags, setTags] = useState({});
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [initialValues, setInitialValues] = useState({
    searchText: '',
    dateRanges: '6',
    customRange: [moment().subtract(1, 'months'), moment().add(5, 'months')],
    meetingTypes: [],
    states: [],
    committees: [],
  });

  const [useUserCommittees, setUseUserCommittees] = useState(false);

  useEffect(() => {
    const { daterange, meetingtype, states, committees, datestart = null, dateend = null } = query;
    if (
      isEmpty(query) &&
      ['search', 'daterange', 'meetingtype'].some(str => router.asPath.indexOf(str) > -1)
    ) {
      // when nextjs statically built load, the query is empty because the router
      // hasn't full loaded yet. This is how we check for this.
      return;
    }

    // eslint-disable-next-line camelcase
    const { page, show, wrap_access_token, wrap_access_token_expires_in, ..._query } = query;

    if (isEmpty(_query)) {
      search({
        dateRanges: '6',
        customRange: [moment().subtract(30, 'days'), moment().add(30, 'days')],
        meetingTypes: [],
        states: [],
        committees: [],
        show: query.show,
      });
    } else {
      // Only allow committees that exist in Committees.entities)
      const unfilteredCommittees = committees ? committees.split(',') : [];
      const committedIds = Object.keys(Committees.entities);
      const filteredCommittees = unfilteredCommittees.filter(c => {
        return committedIds.includes(c);
      });

      const values = {
        searchText: query.search || '',
        meetingTypes: meetingtype ? meetingtype.split(',') : [],
        states: states ? states.split(',') : [],
        committees: filteredCommittees,
        dateRanges: daterange,
        ...(daterange === '6' && { customRange: [moment(datestart), moment(dateend)] }),
        ...(query.myCommittees && { myCommittees: true }),
        show: query.show,
      };

      const newValues = {};
      Object.entries(query).forEach(([key, val]) => {
        const upperKey = key.toUpperCase();
        const fieldName = FIELD_NAMES[upperKey];
        let value = [];

        if (upperKey === 'SEARCH') {
          value = [
            {
              value: val,
              children: val,
              input: FIELD_NAMES.SEARCHTEXT,
            },
          ];
          newValues[`tags_${FIELD_NAMES.SEARCHTEXT}`] = value;
        } else if (['DATESTART', 'DATEEND'].includes(upperKey)) {
          if (!newValues[`tags_customRange`] || !Array.isArray(newValues[`tags_customRange`])) {
            value = [
              {
                value: [undefined, undefined],
                input: 'customRange', // name used in getFieldDecorator
                children: 'DS-DE', // text description
              },
            ];
          } else {
            value = newValues[`tags_customRange`];
          }
          const dtValue = moment(val);
          const isDateStart = upperKey === 'DATESTART';
          value[0].value[isDateStart ? 0 : 1] = dtValue;
          value[0].children = value[0].children.replace(
            isDateStart ? 'DS' : 'DE',
            dtValue.format('MM/DD/YYYY')
          );
          newValues[`tags_customRange`] = value;
        } else if (upperKey === 'DATERANGE') {
          value = [
            {
              value: val,
              children: DateRanges.entities[val][DESCRIPTION_NAME['DATERANGES']],
              input: FIELD_NAMES.DATERANGES,
            },
          ];
          newValues[`tags_${FIELD_NAMES.DATERANGES}`] = value;
        } else if (upperKey === 'MEETINGTYPE') {
          const arraySelections = val.split(',');
          value = arraySelections.map(sel => {
            return {
              value: sel,
              children: MeetingTypes.entities[sel][DESCRIPTION_NAME['MEETINGTYPES']],
              input: FIELD_NAMES.MEETINGTYPES,
            };
          });
          newValues[`tags_${FIELD_NAMES.MEETINGTYPES}`] = value;
        } else if (fieldName && val) {
          const arraySelections = val.split(',');
          value = arraySelections.map(sel => {
            let children = [];

            if (upperKey === 'STATES' && States.entities[sel]) {
              children = States.entities[sel][DESCRIPTION_NAME[upperKey]];
            } else if (Committees.entities[sel]) {
              children = Committees.entities[sel][DESCRIPTION_NAME[upperKey]];
            }

            return {
              value: sel,
              children,
              input: fieldName,
            };
          });
          newValues[`tags_${fieldName}`] = value;
        }
      });
      setTags(newValues);
      setInitialValues(values);
      search(values);

      setUseUserCommittees(!!query.myCommittees);
    }
  }, [
    query.show,
    query.daterange,
    query.meetingtype,
    query.states,
    query.committees,
    query.search,
    query.datestar,
    query.dateend,
    query.myCommittees,
  ]);

  useEffect(() => {
    setIsCustomDateRange(initialValues.dateRanges === '6');
  }, [initialValues.dateRanges]);

  function handleSubmit(e) {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (err) {
        return;
      }
      try {
        const {
          searchText = '',
          dateRanges,
          customRange,
          meetingTypes,
          states,
          committees,
          myCommittees = false,
        } = values;

        let committeeNames = '';
        if (myCommittees && Array.isArray(userCommittees)) {
          committeeNames =
            userCommittees?.length > 0
              ? userCommittees
                .map(committee =>
                  escapeGTMParam(Committees.entities[committee]?.CommitteeDescription || '')
                )
                .join(', ')
              : 'All';
        } else if (Array.isArray(committees)) {
          committeeNames =
            committees?.length > 0
              ? committees
                .map(committee =>
                  escapeGTMParam(Committees.entities[committee].CommitteeDescription)
                )
                .join(', ')
              : 'All';
        }

        trackGTMEvent({
          event: 'meetingSearch',
          text: escapeGTMParam(searchText),
          types:
            Array.isArray(meetingTypes) && meetingTypes.length > 0
              ? meetingTypes
                .map(meeting =>
                  escapeGTMParam(MeetingTypes.entities[meeting].MeetingTypeDescription)
                )
                .join(', ')
              : 'All',
          states: Array.isArray(states) && states?.length > 0 ? states.join(', ') : 'All',
          committees: committeeNames,
          dateRange: DateRanges.entities[dateRanges].DayRangeDescription || 'All',
        });

        const url = formatURL(router.asPath, {
          search: searchText,
          daterange: dateRanges,
          datestart: dateRanges === '6' ? customRange[0].format(URL_DATE_FORMAT) : null,
          dateend: dateRanges === '6' ? customRange[1].format(URL_DATE_FORMAT) : null,
          meetingtype: Array.isArray(meetingTypes) ? meetingTypes.join(',') : null,
          states: Array.isArray(states) ? states.join(',') : null,
          committees: Array.isArray(committees) ? committees.join(',') : null,
          page: 1,
          show: 'upcoming',
          myCommittees,
        });

        if (url !== router.asPath) {
          router.replace(url, url, { shallow: true });
        } else {
          search(values);
        }
      } catch (error) {
        console.log(error);
      }
    });
  }

  const handleReset = () => {
    const { setFieldsValue } = form;

    setFieldsValue({
      searchText: '',
      dateRanges: '6',
      customRange: [moment().subtract(30, 'days'), moment().add(30, 'days')],
      meetingTypes: [],
      states: [],
      committees: [],
      myCommittees: false,
    });
    router.replace('/events/meetings?page=1', '/events/meetings?page=1', { shallow: true });
    setTags(INITIAL_TAG_STATE);
  };

  /**
   *
   * @param {Array} keys empty for collapsed, ['1'] for open;
   */
  function handleCollapseChange(keys) {
    const isOpen = keys?.length > 0;

    trackGTMEvent({
      event: isOpen ? 'expandSearch' : 'collapseSearch',
    });

    setCollapsed(!keys.includes(FILTER_KEY));
  }

  const setSelection = (value, selectedNodes, input) => {
    if (input === 'customRange' && Array.isArray(value) && !value.length) {
      setTags({
        ...tags,
        tags_customRange: [],
      });
    } else if (Array.isArray(value) && value.length === 2 && moment.isMoment(value[1])) {
      setTags({
        ...tags,
        [`tags_${input}`]: [{ value, input, children: selectedNodes.join(' - ') }],
      });
    } else if (selectedNodes && Array.isArray(selectedNodes)) {
      const selections = selectedNodes.map(nd => ({ ...nd.props, input }));
      setTags({
        ...tags,
        [`tags_${input}`]: selections,
      });
    } else {
      setTags({
        ...tags,
        [`tags_${input}`]: value
          ? [{ value, input, children: selectedNodes ? selectedNodes.props.children : value }]
          : [],
      });
    }
  };

  const tagClose = (e, value, input) => {
    e.preventDefault();
    const storedValue = form.getFieldValue(input);
    if (input === FIELD_NAMES.DATESTART) {
      form.setFieldsValue({ customRange: [] });
      setTags({
        ...tags,
        tags_customRange: [],
      });
    } else if (input === FIELD_NAMES.DATERANGES) {
      const newFieldsValue = {
        [input]: undefined,
        customRange: [],
      };
      const newTags = {
        ...tags,
        [`tags_${input}`]: [],
        tags_customRange: [],
      };
      form.setFieldsValue(newFieldsValue);
      setTags(newTags);
      setIsCustomDateRange(false);
    } else if (Array.isArray(storedValue)) {
      const selections = storedValue.filter(v => v !== value);
      form.setFieldsValue({ [input]: selections });
      setTags({
        ...tags,
        [`tags_${input}`]: tags[`tags_${input}`].filter(tag => selections.includes(tag.value)),
      });
    } else {
      form.setFieldsValue({ [input]: undefined });
      setTags({
        ...tags,
        [`tags_${input}`]: [],
      });
    }
  };

  const getTagCount = label => {
    if (tags && tags[label] && tags[label].length) {
      return tags[label].length;
    }
    return 0;
  };

  const handleMyCommitteeChange = e => {
    const {
      target: { checked },
    } = e;

    setUseUserCommittees(checked);

    if (checked) {
      form.setFieldsValue({ committees: undefined });

      setTags({
        ...tags,
        tags_committees: [],
      });
    }
  };

  return (
    <div>
      <Row gutter={{ xs: 24, md: 32 }} type="flex">
        <Col xs={24} md={24} lg={collapsed ? 8 : 24}>
          <div css={style.filters}>
            <Collapse
              {...filterCollapseProps}
              activeKey={collapsed ? [] : [FILTER_KEY]}
              onChange={handleCollapseChange}
              css={filterColorOverride}
            >
              <Panel
                key={FILTER_KEY}
                header={
                  <Heading type={3} weight="bolder" theme="primaryGray">
                    Search Filters
                  </Heading>
                }
                css={style.filterWrapper}
                id={collapsed ? 'collapsepanel' : 'expandpanel'}
              >
                <Form colon={false} onSubmit={handleSubmit}>
                  <div css={style.filterSearch}>
                    <Form.Item css={m.b0}>
                      {form.getFieldDecorator('searchText', {
                        initialValue: initialValues.searchText,
                      })(
                        <Input
                          prefix={<Icon type="search" />}
                          placeholder="Search"
                          css={[formStyle.inputGray, m.r16]}
                          onChange={(e, s) => setSelection(e.target.value, s, 'searchText')}
                        />
                      )}
                    </Form.Item>
                  </div>
                  <FilterSection label="Date Range" keyProp="1" defaultActiveKey={['1']}>
                    <>
                      <Form.Item css={m.b0}>
                        {form.getFieldDecorator('dateRanges', {
                          initialValue: initialValues.dateRanges,
                        })(
                          <Select
                            css={formStyle.inputGray}
                            size="large"
                            optionFilterProp="children"
                            onChange={(value, s) => {
                              setSelection(value, s, 'dateRanges');
                              setIsCustomDateRange(value === '6');
                            }}
                          >
                            {DateRanges.result.map(dr => {
                              const entity = DateRanges.entities[dr];
                              return (
                                <Select.Option
                                  key={entity.DayRangeCode}
                                  value={entity.DayRangeCode}
                                >
                                  {entity.DayRangeDescription}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        )}
                      </Form.Item>
                      {isCustomDateRange ? (
                        <Form.Item css={[m.b0, m.t16]}>
                          {form.getFieldDecorator('customRange', {
                            rules: [
                              {
                                required: true,
                                message: 'A date range is required when selecting custom',
                                validator: validateCustomRange,
                              },
                            ],
                            initialValue: initialValues.customRange,
                          })(
                            <DateRangePicker
                              onChange={(value, s) => setSelection(value, s, 'customRange')}
                            />
                          )}
                        </Form.Item>
                      ) : null}
                    </>
                  </FilterSection>
                  <FilterSection label={`Meeting Types (${getTagCount('tags_meetingTypes')})`}>
                    <Form.Item css={m.b0}>
                      {form.getFieldDecorator('meetingTypes', {
                        initialValue: initialValues.meetingTypes,
                      })(
                        <Select
                          maxTagCount={5}
                          maxTagTextLength={16}
                          css={formStyle.inputGray}
                          mode="multiple"
                          size="large"
                          optionFilterProp="children"
                          placeholder="Select Meeting Types"
                          onChange={(value, s) => setSelection(value, s, 'meetingTypes')}
                        >
                          {MeetingTypes.result
                            .filter(item => item !== '1')
                            .map(mt => {
                              const entity = MeetingTypes.entities[mt];
                              return (
                                <Select.Option
                                  key={entity.MeetingTypeId}
                                  value={entity.MeetingTypeId}
                                >
                                  {entity.MeetingTypeDescription}
                                </Select.Option>
                              );
                            })}
                        </Select>
                      )}
                    </Form.Item>
                  </FilterSection>
                  <FilterSection label={`Jurisdictions (${getTagCount('tags_states')})`}>
                    <Form.Item css={m.b0}>
                      {form.getFieldDecorator('states', {
                        initialValue: initialValues.states,
                      })(
                        <Select
                          maxTagCount={5}
                          maxTagTextLength={16}
                          css={formStyle.inputGray}
                          mode="multiple"
                          size="large"
                          optionFilterProp="children"
                          placeholder="Select Jurisdictions"
                          onChange={(value, s) => setSelection(value, s, 'states')}
                        >
                          {States.result.map(s => {
                            const entity = States.entities[s];
                            return (
                              <Select.Option key={entity.StateCode} value={entity.StateCode}>
                                {entity.StateDescription}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      )}
                    </Form.Item>
                  </FilterSection>
                  <FilterSection
                    label={`Committees  (${useUserCommittees
                      ? userCommittees.length || 0
                      : getTagCount('tags_committees')
                      })`}
                  >
                    <Form.Item>
                      {form.getFieldDecorator('committees', {
                        initialValue: initialValues.committees,
                      })(
                        <Select
                          maxTagCount={5}
                          maxTagTextLength={16}
                          css={formStyle.inputGray}
                          mode="multiple"
                          size="large"
                          optionFilterProp="children"
                          placeholder="Select Committees"
                          onChange={(value, s) => {
                            setSelection(value, s, 'committees');
                            if (value.length) {
                              form.setFieldsValue({ myCommittees: false });
                            }
                          }}
                        >
                          {Committees.result.map(mv => {
                            const entity = Committees.entities[mv];
                            return (
                              <Select.Option
                                key={entity.CommitteeCode}
                                value={entity.CommitteeCode}
                              >
                                {entity.CommitteeDescription}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      )}
                    </Form.Item>
                    <Form.Item css={m.b0}>
                      {form.getFieldDecorator('myCommittees', {
                        initialValue: !!query.myCommittees,
                        valuePropName: 'checked',
                      })(
                        <Checkbox onClick={handleMyCommitteeChange}>
                          <span css={style.myCommitteesLabel}>My Committee Meetings</span>
                          <Tooltip
                            css={{}}
                            title="For committee members, check this box to see your meetings."
                          >
                            <Icon
                              theme="filled"
                              twoToneColor={colors.primaryBlue}
                              type="info-circle"
                              css={style.toolTipIcon}
                            />
                          </Tooltip>
                        </Checkbox>
                      )}
                    </Form.Item>
                  </FilterSection>
                  <div css={style.buttonContainer}>
                    <Button htmlType="submit" size="small" css={[m.r16, m.t16]}>
                      Search
                    </Button>
                    <Button size="small" theme="gray" css={m.t16} onClick={handleReset}>
                      <Icon type="sync" /> Reset All
                    </Button>
                  </div>
                </Form>
              </Panel>
            </Collapse>
          </div>
        </Col>
        <Col
          xs={collapsed ? 16 : 24}
          aria-hidden={!collapsed}
          css={!collapsed ? style.filterTagsHidden : style.filterTagsVisible}
        >
          <div
            css={
              !collapsed
                ? css`
                    ${style.filterTags};
                    visibility: hidden;
                    opacity: 0;
                    max-height: 0px;
                    border-width: 0px;
                  `
                : style.filterTags
            }
          >
            {Object.values(tags).map(tagTypeValues =>
              tagTypeValues.map(tag => <SearchTag key={tag.value} tag={tag} tagClose={tagClose} />)
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default EventsSearchFilters;
